/**
 * Slice 59 — what is still on the phone for one job: photos grouped Before / After with a state each,
 * the checklist answers and the flags. Calm grey, no red. When everything has sent it turns into the
 * "All caught up" card.
 */
import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { idbGet } from "@/lib/offline/idb";
import { getJobQueue, useOutboxVersion, type JobQueuePhoto, type PhotoGroup, type QueueState, type StoredPhoto } from "@/lib/offline/outbox";

function useThumb(p: JobQueuePhoto): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let made: string | null = null;
    let live = true;
    idbGet<StoredPhoto>("photos", `${p.bucket}/${p.path}`)
      .then((s) => {
        if (!live || !s) return;
        made = URL.createObjectURL(s.blob);
        setUrl(made);
      })
      .catch(() => undefined);
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [p.bucket, p.path]);
  return url;
}

const stateKey: Record<QueueState, string> = {
  waiting: "pendStateWaiting",
  sending: "pendStateSending",
  retrying: "pendStateRetrying",
};

function Thumb({ p }: { p: JobQueuePhoto }) {
  const { t } = useTranslation();
  const url = useThumb(p);
  return (
    <div className="relative aspect-square overflow-hidden rounded-[10px] bg-[#e6edf1]">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
      <span
        className={cn(
          "absolute bottom-1 left-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold",
          p.state === "sending" ? "bg-[#24a89a] text-white" : "bg-[rgba(13,20,27,0.72)] text-white",
        )}
      >
        {t(stateKey[p.state])}
      </span>
    </div>
  );
}

export function PendingUploads({ taskId, className }: { taskId: string; className?: string }) {
  const { t } = useTranslation();
  useOutboxVersion();
  const { photos, writes, sent } = getJobQueue(taskId);

  const groups: { key: PhotoGroup; label: string }[] = [
    { key: "before", label: t("pendBefore") },
    { key: "after", label: t("pendAfter") },
    { key: "other", label: t("pendOther") },
  ];
  const sentTotal = sent.before + sent.after + sent.other;
  const nothingLeft = photos.length === 0 && writes.length === 0;

  if (nothingLeft && sentTotal === 0) return null;

  if (nothingLeft) {
    return (
      <div className={cn("flex flex-col gap-3 rounded-[18px] border-[1.5px] border-[#bfe3dd] bg-[#f1f8f7] p-4", className)}>
        <div className="flex items-center gap-2 text-[17px] font-bold text-[#15776d]">
          <Check className="h-5 w-5" strokeWidth={3} />
          {t("pendCaughtUp")}
        </div>
        <div className="text-[14.5px] font-medium text-[#475569]">{t("pendCaughtUpBody")}</div>
        {groups.map((g) =>
          sent[g.key] > 0 ? (
            <div key={g.key} className="flex items-center justify-between border-t border-[#d6ebe7] pt-2 text-[14.5px]">
              <span className="font-medium text-[#64748b]">{g.label}</span>
              <span className="font-bold text-[#0f1720]">{t("pendSentCount").replace("{{n}}", String(sent[g.key]))}</span>
            </div>
          ) : null,
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-4", className)}>
      <div className="text-[17px] font-bold text-[#0f1720]">{t("jobTagWaiting")}</div>
      {groups.map((g) => {
        const list = photos.filter((p) => p.group === g.key);
        if (list.length === 0) return null;
        const done = sent[g.key];
        return (
          <div key={g.key} className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-[14px]">
              <span className="font-bold text-[#0f1720]">{g.label}</span>
              <span className="font-medium text-[#64748b]">
                {t("pendOfSent").replace("{{done}}", String(done)).replace("{{total}}", String(done + list.length))}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {list.map((p) => (
                <Thumb key={p.id} p={p} />
              ))}
            </div>
          </div>
        );
      })}
      {writes.length > 0 ? (
        <div className="flex flex-col gap-1 border-t border-[#e6edf1] pt-2.5">
          {(["checklist", "flag"] as const).map((k) => {
            const list = writes.filter((w) => w.kind === k);
            if (list.length === 0) return null;
            const worst: QueueState = list.some((w) => w.state === "sending") ? "sending" : list.some((w) => w.state === "retrying") ? "retrying" : "waiting";
            return (
              <div key={k} className="flex items-center justify-between text-[14.5px]">
                <span className="font-medium text-[#0f1720]">{k === "checklist" ? t("pendChecklist") : t("pendFlags")}</span>
                <span className="font-medium text-[#64748b]">{t(stateKey[worst])}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
